import { useMemo, useState, type PointerEvent, type ReactNode } from 'react';
import * as fmt from '../lib/format.js';
import {
  ENERGY_WINDOW,
  KCAL_PER_KG_WEEK,
  estimateEnergy,
  goalBand,
  linePath,
  mean,
  movingAverage,
  smoothWeightAt,
  type BodyProfile,
  type DayRow,
  type Goal,
} from '../lib/stats.js';

interface Props {
  series: DayRow[];
  selectedDay: string | null;
  goal: Goal;
  profile: BodyProfile;
  onSelect: (day: string) => void;
  /** Ouvre la journée en saisie : seul chemin vers l’édition au doigt. */
  onOpen: (day: string) => void;
  onOpenInfo: () => void;
}


const W = 1000;
const WEIGHT_H = 200;
const BAR_H = 100;

/**
 * Un item de légende : le trait reprend celui du graphe. « Pointillé = maintien
 * ... » disait la même chose en trois fois plus de caractères — et c'est cette
 * longueur qui finissait par recouvrir les barres.
 */
function BandKey({ variant, children }: { variant: string; children: ReactNode }) {
  return (
    <span className={`band__key band__key--${variant}`}>
      <i aria-hidden="true" />
      {children}
    </span>
  );
}

const RANGES = [
  { days: 30, label: '30 j' },
  { days: 90, label: '90 j' },
  { days: Number.POSITIVE_INFINITY, label: 'Tout' },
];

/**
 * Les trois bandes partagent l'axe des abscisses et le curseur : c'est
 * l'alignement vertical qui porte l'information, on descend du poids vers les
 * pas et les calories du même jour.
 */
export function TimelineCard({
  series,
  selectedDay,
  goal,
  profile,
  onSelect,
  onOpen,
  onOpenInfo,
}: Props) {
  const [rangeDays, setRangeDays] = useState<number>(90);
  const [hover, setHover] = useState<number | null>(null);
  /** Un doigt (ou une souris) posé sur le graphe, qui promène la lecture. */
  const [scrubbing, setScrubbing] = useState(false);

  const model = useMemo(() => {
    const count = Math.max(2, Math.min(rangeDays, series.length));
    const start = series.length - count;
    const view = series.slice(start);
    const last = count - 1;

    // --- Poids : mesure brute, moyenne 7 jours, aire sous la moyenne.
    const average = movingAverage(series, 7).slice(start);
    const raw = view.map((row) => row.weight);
    const known = [...raw, ...average].filter((value): value is number => value != null);
    // Bornes arrondies au demi-kilo, avec 400 g de marge : la courbe ne colle
    // jamais au bord du cadre.
    const low = known.length ? Math.floor(Math.min(...known) * 2) / 2 - 0.4 : 0;
    const high = known.length ? Math.ceil(Math.max(...known) * 2) / 2 + 0.4 : 1;
    const span = high - low || 1;

    const toY = (value: number) => WEIGHT_H - ((value - low) / span) * WEIGHT_H;
    const areaPoints = average
      .map((value, index) => (value == null ? null : [(index / last) * W, toY(value)] as const))
      .filter((point): point is readonly [number, number] => point !== null);

    const ticks = Array.from({ length: 4 }, (_, index) => ({
      label: fmt.weight(high - (span * index) / 3),
      y: (WEIGHT_H * index) / 3,
    }));

    // --- Barres : une par jour, 62 % du pas horizontal pour laisser respirer.
    const slot = W / count;
    const barWidth = slot * 0.62;
    const offset = (slot - barWidth) / 2;
    const stepsMax = Math.max(1, ...view.map((row) => row.steps ?? 0)) * 1.08;

    // Maintien déduit des saisies (apports corrigés de la variation de poids)
    // plutôt que d'une formule ; voir `estimateEnergy`. Toujours sur la fenêtre
    // de référence, jamais sur la plage affichée : le maintien caractérise la
    // personne, pas le cadrage du graphe, et trois cartes affichant trois
    // maintiens différents ne s'expliqueraient pas.
    const energy = estimateEnergy(series, series.length - 1, profile, ENERGY_WINDOW);
    const band = goalBand(goal, smoothWeightAt(series, series.length - 1) ?? profile.startWeightKg);
    const target = energy.tdee + band.target * KCAL_PER_KG_WEEK;

    // Les deux repères entrent dans le cadrage : une cible hors champ, rabattue
    // sur le bord, se lirait comme une cible atteinte.
    const kcalMax =
      Math.max(1, ...view.map((row) => row.kcal ?? 0), energy.tdee, target) * 1.08;

    const bars = (key: 'steps' | 'kcal', max: number) =>
      view.map((row, index) => {
        const value = row[key];
        const height = value == null ? 0 : (value / max) * BAR_H;
        return {
          key: row.day,
          x: index * slot + offset,
          y: BAR_H - height,
          width: barWidth,
          height,
          // Le jour sélectionné ou survolé ressort ; les autres s'effacent.
          opacity: row.day === selectedDay ? 1 : hover === start + index ? 0.95 : 0.5,
        };
      });

    const stepsAverage = mean(view.map((row) => row.steps)) ?? 0;

    const xAt = (index: number) => ((index - start) / last) * W;

    return {
      start,
      count,
      view,
      average,
      energy,
      rawPath: linePath(raw, low, high, W, WEIGHT_H),
      averagePath: linePath(average, low, high, W, WEIGHT_H),
      areaPath: areaPoints.length
        ? `M${areaPoints.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L')} L${W} ${WEIGHT_H} L0 ${WEIGHT_H} Z`
        : '',
      ticks,
      toY,
      xAt,
      stepsBars: bars('steps', stepsMax),
      kcalBars: bars('kcal', kcalMax),
      stepsTicks: [`${Math.round(stepsMax / 1000)}k`, '0'],
      kcalTicks: [fmt.number(Math.round(kcalMax / 100) * 100), '0'],
      stepsAverage,
      target,
      stepsAverageY: BAR_H - (stepsAverage / stepsMax) * BAR_H,
      maintenanceY: BAR_H - (energy.tdee / kcalMax) * BAR_H,
      targetY: BAR_H - (target / kcalMax) * BAR_H,
      xLabels: [0, 0.25, 0.5, 0.75, 1].map((fraction) => {
        const row = view[Math.round(fraction * last)];
        return { key: fraction, label: row ? fmt.date(row.date, { day: '2-digit', month: 'short' }) : '' };
      }),
    };
  }, [series, rangeDays, selectedDay, hover, goal, profile]);

  function indexFromEvent(event: PointerEvent<SVGSVGElement>): number {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    return model.start + Math.round(ratio * (model.count - 1));
  }

  const hoverAverage = hover != null ? model.average[hover - model.start] ?? null : null;
  const hoverX = hover != null ? model.xAt(hover) : 0;
  const selectedIndex = selectedDay ? series.findIndex((row) => row.day === selectedDay) : -1;
  const selectedX = selectedIndex >= model.start ? model.xAt(selectedIndex) : -10;

  // La lecture suit le doigt tant qu'il est posé, puis reste sur le jour
  // sélectionné. C'est tout l'écart avec la version précédente : au doigt, le
  // relâché effaçait la ligne qu'on venait justement d'aller chercher.
  const readIndex = hover ?? (selectedIndex >= model.start ? selectedIndex : series.length - 1);
  const readRow = series[readIndex] ?? null;
  const readAverage = model.average[readIndex - model.start] ?? null;

  /** Bornée à la plage affichée : le curseur ne sort jamais du cadre. */
  const step = (delta: number) => {
    const target = Math.max(model.start, Math.min(series.length - 1, readIndex + delta));
    const row = series[target];
    if (!row) return;
    setHover(null);
    onSelect(row.day);
  };

  const pick = (event: PointerEvent<SVGSVGElement>) => {
    const row = series[indexFromEvent(event)];
    if (row) onSelect(row.day);
  };

  const handlers = {
    // Le jour n'est retenu qu'au relâché : un balayage vertical pour faire
    // défiler la page part souvent du graphe, et il ne doit pas déplacer la
    // sélection au passage. Le navigateur nous envoie alors `pointercancel`.
    onPointerDown: (event: PointerEvent<SVGSVGElement>) => {
      // Capture : le doigt peut déborder du cadre sans perdre le balayage.
      event.currentTarget.setPointerCapture(event.pointerId);
      setScrubbing(true);
      setHover(indexFromEvent(event));
    },
    onPointerMove: (event: PointerEvent<SVGSVGElement>) => {
      setHover(indexFromEvent(event));
      if (scrubbing) pick(event);
    },
    onPointerUp: (event: PointerEvent<SVGSVGElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (!scrubbing) return;
      setScrubbing(false);
      pick(event);
      // Au doigt il n'y a pas de survol après le relâché : la lecture retombe
      // sur le jour sélectionné, c'est-à-dire celui qu'on vient de poser.
      if (event.pointerType !== 'mouse') setHover(null);
    },
    onPointerCancel: () => {
      setScrubbing(false);
      setHover(null);
    },
    onPointerLeave: () => {
      if (!scrubbing) setHover(null);
    },
  };

  /**
   * Curseurs des trois bandes : le trait plein marque le jour retenu, le
   * pointillé le survol en cours. Ils sont tracés dans les trois bandes et non
   * sous la seule courbe du poids — c'est l'alignement vertical qui porte la
   * lecture, et au doigt, sans survol persistant, la barre de pas du jour
   * choisi n'était plus repérable une fois le doigt levé.
   */
  const cursors = (height: number) => (
    <>
      <line
        x1={selectedX}
        x2={selectedX}
        y1="0"
        y2={height}
        stroke="var(--k)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {hover == null ? null : (
        <line
          x1={hoverX}
          x2={hoverX}
          y1="0"
          y2={height}
          stroke="var(--hair)"
          strokeWidth="1"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </>
  );

  return (
    <section className="card timeline">
      <header className="timeline__head">
        <div className="timeline__title">
          <h2 className="card__title">Évolution comparée</h2>
          <button type="button" className="info" onClick={onOpenInfo} title="Comment lire ce graphe">
            i
          </button>
        </div>
        <div className="segmented" role="group" aria-label="Plage affichée">
          {RANGES.map((option) => (
            <button
              key={option.label}
              type="button"
              aria-pressed={rangeDays === option.days}
              onClick={() => {
                setRangeDays(option.days);
                setHover(null);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      {/* Lecture du jour. Elle était un filet de texte survolé, donc inatteignable
          au doigt : elle persiste désormais sur le jour retenu, se déplace d'un
          jour à l'autre au bouton — 90 jours sur 340 px font moins de 4 px par
          barre, viser juste ne marche pas — et ouvre la saisie sans passer par
          le calendrier. */}
      <div className="readout" role="group" aria-label="Journée lue">
        <div className="readout__day">
          <button
            type="button"
            className="btn--icon"
            onClick={() => step(-1)}
            disabled={readIndex <= model.start}
            aria-label="Jour précédent"
          >
            ‹
          </button>
          <span className="readout__date">
            {readRow
              ? fmt.date(readRow.date, { weekday: 'short', day: 'numeric', month: 'short' })
              : '—'}
          </span>
          <button
            type="button"
            className="btn--icon"
            onClick={() => step(1)}
            disabled={readIndex >= series.length - 1}
            aria-label="Jour suivant"
          >
            ›
          </button>
        </div>

        <dl className="readout__values">
          <div>
            <dt>Poids</dt>
            <dd>{readRow?.weight == null ? '—' : `${fmt.measuredWeight(readRow.weight)} kg`}</dd>
          </div>
          <div>
            <dt>Moy. 7 j</dt>
            <dd>{readAverage == null ? '—' : `${fmt.weight(readAverage)} kg`}</dd>
          </div>
          <div>
            <dt>Pas</dt>
            <dd>{fmt.number(readRow?.steps)}</dd>
          </div>
          <div>
            <dt>Kcal</dt>
            <dd>{fmt.number(readRow?.kcal)}</dd>
          </div>
          <div>
            <dt>Séance</dt>
            <dd>{readRow?.gym == null ? '—' : readRow.gym ? 'oui' : 'non'}</dd>
          </div>
        </dl>

        <button
          type="button"
          className="btn btn--sm readout__edit"
          onClick={() => readRow && onOpen(readRow.day)}
          disabled={!readRow}
        >
          Éditer
        </button>
      </div>

      <div className="band">
        <div className="band__caption">
          <span className="band__label">Poids · kg</span>
          <BandKey variant="raw">mesure</BandKey>
          <BandKey variant="average">moyenne 7 j</BandKey>
        </div>
        <div className="band__ticks" aria-hidden="true">
          {model.ticks.map((tick) => (
            <span key={tick.y}>{tick.label}</span>
          ))}
        </div>
        <div className="band__plot">
          <svg
            viewBox={`0 0 ${W} ${WEIGHT_H}`}
            preserveAspectRatio="none"
            style={{ height: 210 }}
            {...handlers}
          >
            {model.ticks.map((tick) => (
              <line
                key={tick.y}
                x1="0"
                x2={W}
                y1={tick.y}
                y2={tick.y}
                stroke="var(--line2)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <path d={model.areaPath} fill="var(--line2)" stroke="none" />
            <path
              d={model.rawPath}
              fill="none"
              stroke="var(--hair)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={model.averagePath}
              fill="none"
              stroke="var(--ink)"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
            {cursors(WEIGHT_H)}
            {hoverAverage != null ? (
              <circle
                cx={hoverX}
                cy={model.toY(hoverAverage)}
                r="3.5"
                fill="var(--ink)"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}
          </svg>
        </div>
      </div>

      <div className="band">
        <div className="band__caption band__caption--steps">
          <span className="band__label">Pas</span>
          <BandKey variant="steps">moyenne {fmt.number(model.stepsAverage)}</BandKey>
        </div>
        <div className="band__ticks" aria-hidden="true">
          {model.stepsTicks.map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
        </div>
        <div className="band__plot">
          <svg viewBox={`0 0 ${W} ${BAR_H}`} preserveAspectRatio="none" style={{ height: 88 }} {...handlers}>
            {model.stepsBars.map((bar) => (
              <rect
                key={bar.key}
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill="var(--s)"
                opacity={bar.opacity}
              />
            ))}
            <line
              x1="0"
              x2={W}
              y1={model.stepsAverageY}
              y2={model.stepsAverageY}
              stroke="var(--s)"
              strokeWidth="1"
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
            {cursors(BAR_H)}
          </svg>
        </div>
      </div>

      <div className="band">
        <div className="band__caption band__caption--kcal">
          <span className="band__label">Kcal</span>
          <BandKey variant="maintenance">
            maintien {model.energy.source === 'observed' ? 'observé' : 'estimé'}{' '}
            {fmt.number(model.energy.tdee)}
          </BandKey>
          <BandKey variant="target">cible {fmt.number(model.target)}</BandKey>
        </div>
        <div className="band__ticks" aria-hidden="true">
          {model.kcalTicks.map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
        </div>
        <div className="band__plot">
          <svg viewBox={`0 0 ${W} ${BAR_H}`} preserveAspectRatio="none" style={{ height: 88 }} {...handlers}>
            {model.kcalBars.map((bar) => (
              <rect
                key={bar.key}
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill="var(--k)"
                opacity={bar.opacity}
              />
            ))}
            <line
              x1="0"
              x2={W}
              y1={model.maintenanceY}
              y2={model.maintenanceY}
              stroke="var(--hair)"
              strokeWidth="1"
              strokeDasharray="4 4"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1="0"
              x2={W}
              y1={model.targetY}
              y2={model.targetY}
              stroke="var(--k)"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
            {cursors(BAR_H)}
          </svg>
        </div>
      </div>

      <div className="timeline__xaxis" aria-hidden="true">
        {model.xLabels.map((label) => (
          <span key={label.key}>{label.label}</span>
        ))}
      </div>
    </section>
  );
}
